/** Okapi BM25 ranking over tokenized documents. */

export type Bm25DocumentMeta = Record<string, unknown>;

export type Bm25SearchResult<T extends Bm25DocumentMeta = Bm25DocumentMeta> = {
  id: string;
  score: number;
  meta: T;
};

type InternalDoc<T extends Bm25DocumentMeta> = {
  id: string;
  tokens: string[];
  length: number;
  meta: T;
};

const K1 = 1.2;
const B = 0.75;

/** Lowercase alphanumeric tokens (min length 2). */
export function tokenize(text: string): string[] {
  const raw = text.toLowerCase().match(/[a-z0-9]{2,}/g);
  if (!raw) return [];
  return raw;
}

export class Bm25Index<T extends Bm25DocumentMeta = Bm25DocumentMeta> {
  private docs: InternalDoc<T>[] = [];
  private df = new Map<string, number>();
  private avgdl = 0;
  private built = false;

  get size(): number {
    return this.docs.length;
  }

  clear(): void {
    this.docs = [];
    this.df.clear();
    this.avgdl = 0;
    this.built = false;
  }

  add(id: string, text: string, meta: T): void {
    const tokens = tokenize(text);
    if (tokens.length === 0) return;
    this.docs.push({ id, tokens, length: tokens.length, meta });
    this.built = false;
  }

  /** Call after all `add` calls before searching. */
  finalize(): void {
    this.df.clear();
    let totalLen = 0;
    for (const doc of this.docs) {
      totalLen += doc.length;
      const seen = new Set<string>();
      for (const t of doc.tokens) {
        if (seen.has(t)) continue;
        seen.add(t);
        this.df.set(t, (this.df.get(t) ?? 0) + 1);
      }
    }
    this.avgdl = this.docs.length > 0 ? totalLen / this.docs.length : 0;
    this.built = true;
  }

  search(query: string, limit = 30): Bm25SearchResult<T>[] {
    if (!this.built) this.finalize();
    const qTokens = tokenize(query);
    if (qTokens.length === 0 || this.docs.length === 0) return [];

    const N = this.docs.length;
    const scores: { id: string; score: number; meta: T }[] = [];

    for (const doc of this.docs) {
      let score = 0;
      const tf = new Map<string, number>();
      for (const t of doc.tokens) tf.set(t, (tf.get(t) ?? 0) + 1);

      for (const term of qTokens) {
        const freq = tf.get(term);
        if (!freq) continue;
        const df = this.df.get(term) ?? 0;
        const idf = Math.log((N - df + 0.5) / (df + 0.5) + 1);
        const denom = freq + K1 * (1 - B + (B * doc.length) / (this.avgdl || 1));
        score += idf * ((freq * (K1 + 1)) / denom);
      }

      if (score > 0) scores.push({ id: doc.id, score, meta: doc.meta });
    }

    scores.sort((a, b) => b.score - a.score);
    return scores.slice(0, limit);
  }
}
